import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Component from './Locations.styles'
import {
  __storybook__,
  entryPointId,
  GOOGLE_DISTANCE_CALC,
  GOOGLE_API_KEY,
} from 'settings/app/constants'
import { Grid, CalculateRouteBtn } from 'components/atoms'
import { Map } from 'components/molecules'
import { HospitalLocations, GeneralLocation } from './partials'
import { useMobileSwitch, useMapPrivacyPolicy } from 'hooks'
import { validCoordinates } from 'settings/app/helpers'
import { getDetailURL } from 'settings/app/urlHelpers'
import { validLatitude, validLongitude } from 'settings/propTypeDefinitions'
import { googleDistanceRequest } from 'settings/xhr/axiosRequests'
import { useTextContext } from 'hocs'
import DOMPurify from 'dompurify'

/**
 * location component
 * variants:
 * => singleLocation (all detail views)
 * => multiLocation (hospitals only)
 * @param type
 * @param locations
 * @param entryPoint
 * @param searchKey
 * @param searchType
 * @param coordinates
 * @returns {JSX.Element}
 * @constructor
 */
const Locations = ({
  type,
  locations,
  entryPoint,
  searchKey,
  searchType,
  coordinates,
}) => {
  const { wantGoogleMaps } = useMapPrivacyPolicy()
  const [mapFullScreen, setMapFullScreen] = React.useState(false)
  const isMobile = useMobileSwitch()
  const [mapHeight, setMapHeight] = React.useState('375px') // adjust map height if all locations are shown
  const [searchLocationDistance, setSearchLocationDistance] = useState(false) // exact distance to search location
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false)
  const textContext = useTextContext()

  /**
   * condition for Google Maps api request (day and night cares only)
   * @type {boolean}
   */
  const validDistanceConditions =
    wantGoogleMaps &&
    isGoogleLoaded &&
    GOOGLE_DISTANCE_CALC.includes(entryPoint) &&
    validCoordinates(coordinates.lat, coordinates.lng) &&
    Array.isArray(locations) &&
    locations.length > 0 &&
    validCoordinates(locations[0].marker.lat, locations[0].marker.lon) &&
    !searchLocationDistance &&
    !__storybook__ // avoid xhrs on storybook

  const toggleAllLocation = (showAllLocations) => {
    if (!isMobile) {
      setMapHeight(showAllLocations ? '100%' : '375px')
    }
  }
  /**
   * performe google maps api call
   */
  useEffect(() => {
    if (validDistanceConditions) {
      googleDistanceRequest({
        origin: coordinates,
        destinations: [
          {
            lat: locations[0].marker.lat,
            lng: locations[0].marker.lon,
          },
        ],
      }).then((r) => {
        if (r.length > 0) {
          setSearchLocationDistance(r[0])
        }
      })
    }
    if (__storybook__ && entryPointId.DAYANDNIGHTCARE === entryPoint) {
      setSearchLocationDistance(Math.floor(Math.random() * 10).toString())
    }
  }, [coordinates, entryPoint, locations, validDistanceConditions])

  const getAddressContent = () => {
    if (!locations) return null

    switch (type) {
      case 'singleLocation':
        return (
          <GeneralLocation
            address={ locations[0].address }
            accessibility={ locations[0].accessibility }
            marker={ locations[0].marker }
            areaServed={ locations[0].areaServed }
            searchLocationDistance={ searchLocationDistance }
            entryPoint={ entryPoint }
          />
        )

      case 'multiLocation':
        // TODO add searchType, searchKey und entryPoint to generate detail urls
        return (
          <HospitalLocations
            toggleAllLocation={ (bool) => toggleAllLocation(bool) }
            locations={ locations }
            entryPoint={ entryPoint }
            searchKey={ searchKey }
            searchType={ searchType }
          />
        )

      default:
        return null
    }
  }

  const getMapWithMarkers = () => {
    if (!locations) return null
    return (
      <Map
        id={ `${ type }-map` }
        data-cy={ `${ type }-map` }
        apiKey={ GOOGLE_API_KEY }
        className='location-map'
        defaultCenter={
          locations[0].marker
            ? {
                lat: locations[0].marker.lat,
                lng: locations[0].marker.lon,
              }
            : null
        }
        markers={ locations.map((location) => ({
          lat: location.marker ? location.marker.lat : null,
          lng: location.marker ? location.marker.lon : null,
          title: location.address.title,
          street: location.address.street,
          zip: location.address.zip,
          city: location.address.city,
          phone: location.address.phone,
          active: true,
          showAlphanumericMarker: true,
          detailUrl: location.locationId
            ? getDetailURL({
                id: location.locationId,
                entryPoint,
                city: location.address.city,
                zip: location.address.zip,
                key: searchKey,
                searchTerm: searchType,
              })
            : null,
        })) }
        isMapFullscreen={ mapFullScreen }
        onToggleFullscreen={ () => setMapFullScreen(!mapFullScreen) }
        height={ mapHeight }
        mapPosition='detailView'
        showAlphanumericMarker={ 'multiLocation' === type }
        onMarkerClicked={ () => {} }
        onMarkerHovered={ () => {} }
        onLoad={ () => setIsGoogleLoaded(true) }
      />
    )
  }

  const getAdditionalInfo = () => {
    const additionalInfoElement = []

    if (
      type === 'singleLocation' &&
      Array.isArray(locations) &&
      locations.length > 0 &&
      locations[0].additionalInfo
    ) {
      const [{ additionalInfo }] = locations

      if (typeof additionalInfo === 'object') {
        Object.keys(additionalInfo).forEach((key) => {
          const infoItem = additionalInfo[key]

          // section title only when data is provided
          if (textContext[`generalLocation.additionalInfo.${ key }`]) {
            if (
              (key === 'shops' &&
                (!!infoItem?.distance ||
                  !!infoItem?.inCloseProximity ||
                  !!infoItem?.inFacility)) ||
              (key === 'transportConnectionInfos' &&
                infoItem.length > 0 &&
                infoItem.filter((item) => !!item?.landmark).length > 0)
            ) {
              additionalInfoElement.push(
                <p key={ `${ key }-headline` }>
                  <b>{textContext[`generalLocation.additionalInfo.${ key }`]}</b>
                </p>
              )
            }
          }

          // traffic connection
          if (Array.isArray(infoItem)) {
            infoItem.forEach((infoItem, i) => {
              if (!!infoItem?.landmark) {
                additionalInfoElement.push(
                  <p key={ `${ key }-${ i }` }>
                    {infoItem?.landmark}: {infoItem?.distance}
                  </p>
                )
              }
            })
          }

          // shopping possibilities
          if (typeof infoItem === 'object' && !Array.isArray(infoItem)) {
            if (key === 'shops') {
              if (infoItem.inFacility != false && infoItem.inFacility != null) {
                additionalInfoElement.push(
                  <p
                    key={ `shops-inFacility` }
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        `${
                          textContext[
                            `generalLocation.additionalInfo.shops.inFacility`
                          ]
                        } ${ infoItem.inFacility }`
                      ),
                    }}></p>
                )
              }
              if (
                infoItem.inCloseProximity != false &&
                infoItem.inCloseProximity != null
              ) {
                additionalInfoElement.push(
                  <p
                    key={ `shops-inCloseProximity` }
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        `${
                          textContext[
                            `generalLocation.additionalInfo.shops.inCloseProximity`
                          ]
                        } ${ infoItem.inCloseProximity }`
                      ),
                    }}></p>
                )
              }
              if (infoItem.distance != false && infoItem.distance != null) {
                additionalInfoElement.push(
                  <p key={ `shops-distance` }>
                    {
                      textContext[
                        `generalLocation.additionalInfo.shops.distance`
                      ]
                    }
                    {infoItem.distance}
                  </p>
                )
              }
            } else {
              Object.keys(infoItem).forEach((infoItemKey) => {
                if (infoItem[infoItemKey]) {
                  additionalInfoElement.push(
                    <p key={ `${ key }-${ infoItemKey }` }>
                      {
                        textContext[
                          `generalLocation.additionalInfo.${ key }.${ infoItemKey }`
                        ]
                      }
                      {infoItem[infoItemKey]}
                    </p>
                  )
                }
              })
            }
          }
        })
        // TODO remove after finished abs3 when no more required
      } else {
        additionalInfoElement.push(
          <span
            key={ 'additionalInfoElement' }
            dangerouslySetInnerHTML={{
              __html: locations[0].additionalInfo,
            }}
          />
        )
      }
    }

    return additionalInfoElement.length > 0 ? additionalInfoElement : null
  }

  return (
    <Component.Locations>
      <Grid container containerHeight={{ value: 100, unit: '%' }}>
        <Grid item xs={ 12 } sm={ 5 } md={ 4 } print={ 4 }>
          {getAddressContent()}
        </Grid>

        {((!isMobile && !mapFullScreen) || (isMobile && !mapFullScreen)) && (
          <Grid item xs={ 12 } sm={ 7 } md={ 8 } print={ 8 }>
            {getMapWithMarkers()}
            {getAdditionalInfo()}
          </Grid>
        )}

        {isMobile && 'singleLocation' === type && locations[0].marker && (
          <CalculateRouteBtn marker={ locations[0].marker } />
        )}

        {mapFullScreen && (
          <Grid container>
            <Grid item xs={ 12 } sm={ 12 } md={ 12 } lg={ 12 } xl={ 12 }>
              {getMapWithMarkers()}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Component.Locations>
  )
}

Locations.defaultProps = {
  type: 'singleLocation',
}

Locations.propTypes = {
  /** accordion typ, could be used for test ids and to control content rendering differences **/
  type: PropTypes.oneOf(['singleLocation', 'multiLocation']).isRequired,
  /** location data **/
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      address: PropTypes.shape({
        title: PropTypes.string,
        street: PropTypes.string.isRequired,
        zip: PropTypes.string.isRequired,
        city: PropTypes.string.isRequired,
      }).isRequired,
      marker: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
      }),
      accessibility: PropTypes.oneOfType([
        PropTypes.shape({
          distance: PropTypes.string,
          nextStop: PropTypes.string,
          freeParking: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        }),
        PropTypes.bool,
      ]),
      areaServed: PropTypes.string,
      additionalInfo: PropTypes.oneOfType([
        PropTypes.shape({
          distance: PropTypes.string,
          inCloseProximity: PropTypes.string,
          inFacility: PropTypes.string,
        }),
        PropTypes.arrayOf({
          landmark: PropTypes.string,
          distance: PropTypes.string,
        }),
        PropTypes.string,
      ]),
    })
  ),
  // entryPoint is set from outside because
  // at the detail-page the entryPoint is generated out of the url
  // e.g. if the search was triggered by the cross-search (the entry-point) would be 'cross'
  // but required for the detail uls is 'hospitals' or 'doctors'
  entryPoint: PropTypes.string.isRequired,
  searchKey: PropTypes.string,
  searchType: PropTypes.string,
  coordinates: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.bool, validLatitude]),
    lng: PropTypes.oneOfType([PropTypes.bool, validLongitude]),
  }),
}

export default Locations
